/**
 * Centralized API Configuration
 * 
 * This file provides a single source of truth for API URLs.
 * It reads from environment variables and provides fallbacks for development.
 */

import { API_CONFIG } from './api.config';

/**
 * Get the backend API base URL
 * Priority:
 * 1. window.__BACKEND_ORIGIN__ (set at runtime)
 * 2. Centralized API_CONFIG
 */
export function getApiBaseUrl(): string {
  // Check for runtime configuration
  if (typeof window !== 'undefined' && (window as any).__BACKEND_ORIGIN__) {
    return (window as any).__BACKEND_ORIGIN__;
  }

  // Use centralized configuration
  return API_CONFIG.getBackendOrigin();
}

/**
 * Get the API base URL with /api suffix
 */
export function getApiUrl(): string {
  const base = getApiBaseUrl();
  return base.endsWith('/api') ? base : `${base}/api`;
}

/**
 * Construct full API endpoint URL
 * @param endpoint - API endpoint path (with or without leading slash)
 * @returns Full URL to the API endpoint
 */
export function getApiEndpoint(endpoint: string): string {
  const base = getApiUrl();
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
}

/**
 * Construct full media URL (for images, videos, documents)
 * @param path - Media path (can be relative or absolute)
 * @returns Full URL to the media file
 */
export function getMediaUrl(path: string | null | undefined): string {
  if (!path) return '';
  
  // If already absolute URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Construct full URL from backend origin
  const base = getApiBaseUrl();
  const mediaPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${mediaPath}`;
}

// Export default API base for backward compatibility
export const API_BASE = getApiBaseUrl();
export const API_URL = getApiUrl();
